"use client"

import type { ReactNode } from "react"
import { useEffect } from "react"
import Providers from "./providers"
import { SidebarProvider } from "@/components/ui/sidebar"
import { useAuth } from "@/contexts/AuthContext"
import AppSidebar from "@/components/core/AppSidebar"
import Spinner from "@/components/Spinner"
import { useTenantModules } from "@/hooks/useTenantModules"
import { getModuleHref, hasModule } from "@/lib/modules"

function LayoutContent({ children }: { children: ReactNode }) {
  const { isLoading, isLoggedIn, currentTenantId } = useAuth()
  const { modules, isLoading: modulesLoading } = useTenantModules(
    currentTenantId,
    !isLoading && isLoggedIn && Boolean(currentTenantId),
  )
  const hasAccess = hasModule(modules, "core")

  useEffect(() => {
    if (!isLoading && !modulesLoading && isLoggedIn && !hasAccess && modules[0]) {
      window.location.replace(getModuleHref(modules[0]))
    }
  }, [hasAccess, isLoading, isLoggedIn, modules, modulesLoading])

  if (isLoading || modulesLoading || (isLoggedIn && !hasAccess && modules.length > 0)) {
    return (
      <div className="flex h-dvh w-full items-center justify-center bg-background">
        <Spinner />
      </div>
    )
  }

  if (isLoggedIn && !hasAccess) {
    return (
      <div className="flex h-dvh w-full items-center justify-center bg-background text-sm text-muted-foreground">
        No tienes acceso a ningún módulo.
      </div>
    )
  }

  return isLoggedIn ? (
    <SidebarProvider>
      <div className="flex h-dvh w-full overflow-hidden">
        <AppSidebar />
        <main className="h-full min-h-0 w-full flex-1 overflow-hidden">
          {children}
        </main>
      </div>
    </SidebarProvider>
  ) : (
    <>{children}</>
  )
}

export default function RootLayout({ children }: { children: ReactNode }) {
  return (
    <Providers>
      <LayoutContent>{children}</LayoutContent>
    </Providers>
  )
}
